import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { TranslateContext } from "../../context/TranslateContext";
import { useCustomTheme } from "../../utils/useCustomTheme";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import Content from "./Content";
import {
  Dashboard as DashboardIcon,
  Face2,
  LibraryMusic,
  SupportAgent,
  HistoryToggleOff,
  NotificationsActive,
  Logout,
  CreditCardOutlined,
  ScheduleSendOutlined,
  LightbulbOutlined,
  VideocamOutlined,
  VideoLibraryOutlined,
  ContentCutOutlined,
  RecordVoiceOverOutlined,
  GraphicEqOutlined,
  PsychologyOutlined,
  GroupsOutlined,
} from "@mui/icons-material";
import { UserProvider } from "../../context/UserContext";
import { GlobalContext } from "../../context/GlobalContext";
import { BackgroundJobsProvider } from "../../context/BackgroundJobsContext";
import { ProductTourProvider } from "../../common/tours";
import WelcomeCreditsModal from "../billing/WelcomeCreditsModal";

const drawerWidth = 240;

const UserDashboard = () => {
  const { lang } = React.useContext(TranslateContext);
  const location = useLocation();
  const history = useHistory();
  const [web, setWeb] = React.useState({});
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();
  const { toggleColorMode, isDark } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await hitAxios({
        path: "/api/user/get_me",
        post: false,
        admin: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (!cancelled && res?.data?.success && !res.data.data?.has_paid_plan) {
        history.replace("/pricing");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [history, hitAxios]);

  async function handleGetWeb() {
    const res = await hitAxios({
      path: "/api/web/get_web_public",
      post: false,
      admin: false,
    });
    if (res.data.success) setWeb(res.data.data);
  }

  const menuItems = [
    {
      id: "dashboards",
      text: lang?.dashboard || "Dashboard",
      icon: <DashboardIcon />,
    },
    {
      id: "explore-ideas",
      text: lang?.exploreIdeasNav || "Explore Ideas",
      icon: <LightbulbOutlined />,
    },
    {
      id: "influencers",
      text: lang?.imageStudioNav || "Image Studio",
      icon: <Face2 />,
    },
    {
      id: "video-studio",
      text: lang?.videoStudioNav || "Video Studio",
      icon: <VideocamOutlined />,
    },
    {
      id: "video-library",
      text: lang?.videoLibraryNav || "Video Library",
      icon: <VideoLibraryOutlined />,
    },
    {
      id: "voices",
      text: lang?.voicesNav || "Voices",
      icon: <GraphicEqOutlined />,
    },
    {
      id: "ai-clipping",
      text: lang?.aiClippingNav || "AI Clipping",
      icon: <ContentCutOutlined />,
    },
    {
      id: "music",
      text: lang?.musicNav || "Music",
      icon: <LibraryMusic />,
    },
    {
      id: "voice-changer",
      text: lang?.voiceChangerNav || "Voice Changer",
      icon: <RecordVoiceOverOutlined />,
    },
    {
      id: "research-chat",
      text: lang?.researchChatNav || "Research Chat",
      icon: <PsychologyOutlined />,
    },
    {
      id: "social-scheduler",
      text: lang?.socialSchedulerNav || "Scheduler",
      icon: <ScheduleSendOutlined />,
    },
    {
      id: "billing",
      text: lang?.billingNav || "Billing",
      icon: <CreditCardOutlined />,
    },
    {
      id: "affiliate",
      text: lang?.affiliateNav || "Affiliate",
      icon: <GroupsOutlined />,
    },
    {
      id: "usage",
      text: lang?.usage || "Usage",
      icon: <HistoryToggleOff />,
    },
    {
      id: "notification",
      text: lang?.notification || "Notification",
      icon: <NotificationsActive />,
    },
    {
      id: "help",
      text: lang?.help || "Help",
      icon: <SupportAgent />,
    },
    {
      id: "logout",
      text: lang?.logout || "Logout",
      icon: <Logout />,
    },
  ];

  /* Hidden from nav (kept for reference / future re-enable):
    actors, gallery, content, script-to-video, ugc-ads-workflow,
    video-agent, makeugc
  */
  const getInitialMenu = () => {
    let page = new URLSearchParams(window.location.search).get("page");
    if (page === "b-roll-studio") page = "video-agent";
    if (
      page &&
      menuItems.some(
        (i) => i.id === page || i.submenu?.some((s) => s.id === page),
      )
    )
      return page;
    return "explore-ideas";
  };

  const [selectedMenu, setSelectedMenu] = React.useState(getInitialMenu);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openSubmenus, setOpenSubmenus] = React.useState(() => {
    const init = getInitialMenu();
    const open = {};
    menuItems.forEach((item) => {
      if (item.submenu?.some((s) => s.id === init)) open[item.id] = true;
    });
    return open;
  });

  React.useEffect(() => {
    let page = new URLSearchParams(location.search).get("page");
    if (page === "b-roll-studio") page = "video-agent";
    if (
      page &&
      menuItems.some(
        (i) => i.id === page || i.submenu?.some((s) => s.id === page),
      )
    ) {
      setSelectedMenu(page);
      menuItems.forEach((item) => {
        if (item.submenu?.some((s) => s.id === page))
          setOpenSubmenus((prev) => ({ ...prev, [item.id]: true }));
      });
    }
  }, [location.search]);

  const updateURL = (id) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", id);
    params.delete("checkout");
    window.history.pushState({}, "", `${window.location.pathname}?${params}`);
  };

  const handleMenuClick = (id, hasSubmenu) => {
    if (hasSubmenu) {
      setOpenSubmenus((prev) => ({ ...prev, [id]: !prev[id] }));
    } else {
      setSelectedMenu(id);
      updateURL(id);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleSubmenuClick = (id) => {
    setSelectedMenu(id);
    updateURL(id);
    if (isMobile) setMobileOpen(false);
  };

  React.useEffect(() => {
    const onPop = () => {
      let page = new URLSearchParams(window.location.search).get("page");
      if (page === "b-roll-studio") page = "video-agent";
      if (page) {
        setSelectedMenu(page);
        menuItems.forEach((item) => {
          if (item.submenu?.some((s) => s.id === page))
            setOpenSubmenus((prev) => ({ ...prev, [item.id]: true }));
        });
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  React.useEffect(() => {
    handleGetWeb();
  }, []);

  return (
    <UserProvider>
      <BackgroundJobsProvider>
        <ProductTourProvider
          selectedMenu={selectedMenu}
          onNavigate={(pageId) => {
            setSelectedMenu(pageId);
            updateURL(pageId);
            if (isMobile) setMobileOpen(false);
          }}
        >
          <Box
            sx={{
              display: "flex",
              minHeight: "100vh",
              bgcolor: "background.default",
            }}
          >
            <Sidebar
              lang={lang}
              theme={theme}
              isMobile={isMobile}
              selectedMenu={selectedMenu}
              openSubmenus={openSubmenus}
              mobileOpen={mobileOpen}
              handleDrawerToggle={() => setMobileOpen((p) => !p)}
              handleMenuClick={handleMenuClick}
              handleSubmenuClick={handleSubmenuClick}
              menuItems={menuItems}
              web={web}
            />

            <Box
              component="main"
              sx={{
                flexGrow: 1,
                width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TopBar
                lang={lang}
                theme={theme}
                isMobile={isMobile}
                isDark={isDark}
                selectedMenu={selectedMenu}
                toggleColorMode={toggleColorMode}
                handleDrawerToggle={() => setMobileOpen((p) => !p)}
                web={web}
                onGoBilling={() => {
                  setSelectedMenu("billing");
                  const params = new URLSearchParams(window.location.search);
                  params.set("page", "billing");
                  params.delete("checkout");
                  window.history.pushState(
                    {},
                    "",
                    `${window.location.pathname}?${params}`,
                  );
                }}
              />

              {isMobile && <Box sx={{ ...theme.mixins.toolbar }} />}

              <Box sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1 }}>
                <Content selectedMenu={selectedMenu} web={web} />
              </Box>
            </Box>
          </Box>
          <WelcomeCreditsModal lang={lang} />
        </ProductTourProvider>
      </BackgroundJobsProvider>
    </UserProvider>
  );
};

export default UserDashboard;
